import { Box } from "@adfenix/alexandria";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Card from "components/Card";
import { toast } from "react-toastify";
import { useFacebook, PageData } from "api/services/useFacebook";
import { Scopes, MandatoryScopes } from "api/services/facebookService";
import {
  addFacebookIntegrationConfig,
  updateFacebookIntegrationConfig,
  getFacebookIntegrationConfig,
  getVerifiedFacebookAccount,
  connectAdfenixBusinessToCustomePage as connectAdfenixBusinessToCustomerPage
} from "api/services";
import Meta from "static/svg/social/meta";
import SuccessPage from "./SuccessPage";
import AskingPermissionPage from "./AskingPermissionPage";
import NoPage from "./NoPage";
import OnePage from "./OnePage";
import PageConnected from "./PageConnected";
import PickPage from "./PickPage";
import Loading from "./Loading";
import ConnectPage from "./ConnectPage";
import { FacebookIntegrationsFormTypes, PageInfo, State } from "./interfaces";
import { useTranslation } from "react-i18next";
import OfficeOverview from "./OfficeOverview";
import Sentry from "lib/sentry/init";

const FacebookHqIntegration = ({
  hqId,
  officeId
}: FacebookIntegrationsFormTypes) => {
  const { t } = useTranslation();
  const {
    login,
    getPages,
    validatePermissions,
    getConnectedInstagramAccounts
  } = useFacebook();
  const [activeState, setActiveState] = useState<State>("loading");

  const [loading, setLoading] = useState<boolean>(false);

  const [errorMessageTitle, setErrorMessageTitle] = useState<
    string | undefined
  >(undefined);

  const [errorMessageBody, setErrorMessageBody] = useState<string | undefined>(
    undefined
  );

  const { data: existingHqIntegrationData, refetch } = useQuery(
    ["getFacebookIntegrationConfig", hqId],
    () => getFacebookIntegrationConfig(hqId)
  );

  useEffect(() => {
    if (existingHqIntegrationData?.facebookPageId) {
      if (!officeId) {
        setUrlToVerify(existingHqIntegrationData?.facebookPageUrl);
        setActiveState("pageConnected");
      }
    } else setActiveState("connect");
  }, [
    existingHqIntegrationData?.facebookPageId,
    existingHqIntegrationData?.facebookPageUrl,
    officeId
  ]);

  const { data: existingOfficeIntegrationData, refetch: refetchOffice } =
    useQuery(
      ["getFacebookIntegrationConfig", officeId],
      () => getFacebookIntegrationConfig(officeId ?? ""),
      {
        enabled: Boolean(officeId)
      }
    );

  useEffect(() => {
    if (existingOfficeIntegrationData?.facebookPageId) {
      setUrlToVerify(existingOfficeIntegrationData?.facebookPageUrl);
      setActiveState("pageConnected");
    } else setActiveState("connect");
  }, [
    existingOfficeIntegrationData?.facebookPageId,
    existingOfficeIntegrationData?.facebookPageUrl
  ]);

  const [pagesData, setPagesData] = useState<PageInfo[]>();

  const { mutateAsync: updateFacebookIntegration } = useMutation(
    officeId
      ? existingOfficeIntegrationData?.subjectId
        ? updateFacebookIntegrationConfig
        : addFacebookIntegrationConfig
      : existingHqIntegrationData?.subjectId
      ? updateFacebookIntegrationConfig
      : addFacebookIntegrationConfig,
    {
      retry: false,
      onError: () => {
        toast.error(t("selfonboarding.message.error.failedGetingData"));
      }
    }
  );

  const { mutateAsync: connectPageToAdfenix } = useMutation(
    connectAdfenixBusinessToCustomerPage,
    {
      retry: false
    }
  );

  const [urlToVerify, setUrlToVerify] = useState<string | undefined>(
    existingHqIntegrationData?.facebookPageUrl
  );

  const {
    data: verifiedFacebookAccount,
    isFetching: loadingVerifiedFacebookAccount
  } = useQuery(
    ["getVerifiedFacebookAccount", encodeURIComponent(urlToVerify ?? "")],
    () =>
      getVerifiedFacebookAccount(
        encodeURIComponent(urlToVerify ?? ""),
        existingHqIntegrationData?.businessManagerId ?? ""
      ),
    {
      retry: false,
      enabled: Boolean(urlToVerify && urlToVerify.length > 0)
    }
  );

  const handleLogin = async () => {
    setActiveState("askingPermission");
    setErrorMessageTitle(undefined);
    setErrorMessageBody(undefined);

    try {
      const resp = await login(Scopes);

      if (resp.status === "connected") {
        const missingScopes = validatePermissions(
          MandatoryScopes,
          resp.authResponse.grantedScopes ?? ""
        );

        if (missingScopes.length > 0) {
          Sentry.captureException(
            `Self onboarding failed due to missing scopes: ${missingScopes.join(
              ", "
            )}`
          );
          throw t("selfonboarding.message.error.notEnoughPermission");
        }

        const pages = await getPages(resp.authResponse.userID);

        if (pages.data.length == 0) {
          Sentry.captureException(
            `Self onboarding failed due to missing pages.`
          );
          throw t("selfonboarding.message.error.noAccessToAnyPage");
        }

        const pageData = await getPageObject(pages.data);
        setPagesData(pageData);

        setActiveState("pickPage");
      } else {
        Sentry.captureException(
          `Self onboarding failed due to incorrect login status ${resp.status}.`
        );
        throw t("selfonboarding.message.error.generalError");
      }
      // eslint-disable-next-line
    } catch (e: any) {
      setErrorMessageBody(e.toString());
      if (
        (existingHqIntegrationData && !officeId) ||
        (officeId && existingOfficeIntegrationData)
      )
        setActiveState("pageConnected");
      else setActiveState("connect");
    }
  };

  const getPageObject = async (pages: PageData[]) => {
    try {
      const result: PageInfo[] = await Promise.all(
        pages.map(async page => {
          const instagramPage = await getConnectedInstagramAccounts(
            page.id,
            page.access_token
          );
          return {
            facebook: {
              facebookPageUrl: page.link,
              facebookPageName: page.name,
              facebookPageId: page.id,
              facebookProfilePicture: page.picture.data.url,
              accessToken: page.access_token
            },
            instagram: instagramPage?.data[0]
              ? {
                  instagramActorId: instagramPage.data[0].id,
                  instagramAccountName: instagramPage.data[0].username,
                  instagramProfilePicture: instagramPage.data[0].profile_pic
                }
              : undefined
          };
        })
      );
      return result.sort();
    } catch (e: any) {
      setErrorMessageBody(e.toString());
      if (
        (existingHqIntegrationData && !officeId) ||
        (officeId && existingOfficeIntegrationData)
      )
        setActiveState("pageConnected");
      else setActiveState("connect");
    }
  };

  const handleSaveSetting = async (pageId: string) => {
    setLoading(true);
    const page = pagesData?.find(x => x.facebook.facebookPageId == pageId);
    if (page) {
      try {
        await connectPageToAdfenix({
          pageId: page?.facebook.facebookPageId,
          pageAccessToken: page?.facebook.accessToken,
          businessManagerId: existingHqIntegrationData?.businessManagerId ?? ""
        });

        await updateFacebookIntegration({
          ...(officeId
            ? existingOfficeIntegrationData
            : existingHqIntegrationData),
          facebookPageUrl: page?.facebook.facebookPageUrl,
          subjectId: officeId ? officeId : hqId,
          hqId,
          subjectType: officeId ? "Office" : "Hq",
          facebookPageId: page?.facebook.facebookPageId,
          instagramActorId: page.instagram?.instagramActorId,
          allowOfficeUsersToConfigureSettings: officeId
            ? false
            : existingHqIntegrationData?.allowOfficeUsersToConfigureSettings ??
              false,
          businessManagerId: existingHqIntegrationData?.businessManagerId,
          haveInstagramAccountInDb:
            existingHqIntegrationData?.haveInstagramAccountInDb,
          offlineEventSetId: existingHqIntegrationData?.offlineEventSetId
        });

        setPagesData([]);
        setActiveState("successPage");
      } catch (err: any) {
        Sentry.captureException(err);
        setErrorMessageTitle(t("selfonboarding.message.error.failed.title"));
        setErrorMessageBody(t("selfonboarding.message.error.failed.body"));
        if (
          (existingHqIntegrationData && !officeId) ||
          (officeId && existingOfficeIntegrationData)
        )
          setActiveState("pageConnected");
        else setActiveState("connect");
      }
      setLoading(false);
    }
  };

  const SelectProperPage = ({ pages }: { pages: PageInfo[] }) => {
    if (pages?.length == 0) return <NoPage />;
    if (pages?.length == 1)
      return (
        <OnePage
          page={pages[0]}
          isLoadingUpdate={loading}
          setActiveState={setActiveState}
          saveSetting={handleSaveSetting}
        />
      );
    return (
      <PickPage
        pages={pages}
        isLoadingUpdate={loading}
        saveSetting={handleSaveSetting}
        setActiveState={setActiveState}
      />
    );
  };

  const refetchData = () => {
    refetch();
    if (officeId) refetchOffice();
  };

  const renderState = (currentState: State) => {
    switch (currentState) {
      case "connect":
        return (
          <ConnectPage
            errorMessageTitle={errorMessageTitle}
            errorMessageBody={errorMessageBody}
            officeId={officeId}
            existingHqIntegrationData={existingHqIntegrationData}
            handleLogin={handleLogin}
          />
        );
      case "pageConnected":
        if (
          (existingHqIntegrationData && !officeId) ||
          (existingHqIntegrationData &&
            officeId &&
            existingOfficeIntegrationData)
        )
          return (
            <PageConnected
              errorMessageBody={errorMessageBody}
              errorMessageTitle={errorMessageTitle}
              hqFacebookIntegration={existingHqIntegrationData}
              officeFacebookIntegration={existingOfficeIntegrationData}
              handleLogin={handleLogin}
              verifiedFacebookAccount={verifiedFacebookAccount}
              loadingVerifiedFacebookAccount={loadingVerifiedFacebookAccount}
            />
          );
        return <Loading />;

      case "askingPermission":
        return <AskingPermissionPage />;
      case "pickPage":
        return <SelectProperPage pages={pagesData ?? []} />;
      case "successPage":
        return <SuccessPage setActiveState={setActiveState} />;
      default:
        refetchData();
        return <Loading />;
    }
  };

  return (
    <>
      <Card style={{ minHeight: 300 }}>
        <Box display="block" justifyContent="flex-start">
          <Meta />
        </Box>

        {renderState(activeState)}
      </Card>

      {!officeId &&
        existingHqIntegrationData &&
        activeState == "pageConnected" && (
          <Card style={{ minHeight: 300 }}>
            {!officeId && (
              <OfficeOverview
                hqIntegration={existingHqIntegrationData}
                hqId={hqId}
              />
            )}
          </Card>
        )}
    </>
  );
};

export default FacebookHqIntegration;
